import {
  Badge,
  Box,
  Divider,
  Flex,
  Heading,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Spacer,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { faComment, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'urql';

import { useFlag } from '@unleash/proxy-client-react';
import { useHotkeys } from 'react-hotkeys-hook';
import Comment from '../comments/Comment';

import { CommentsContext } from '../../App';
import {
  CommentDeepFragment,
  ListAllCommentsDocument,
} from '../../gql/graphql';
import { useUserTenant } from '../../services/auth-info';
import SparkelIcon from '../common/icon/SparkelIcon';
import { useViewerMode, ViewerMode } from '../common/viewer-mode';
import NewCommentPopover from './NewCommentPopover';

type ProjectPathParams = {
  projectId: string;
};

export default function CommentsPopover(): React.ReactElement {
  const {
    isOpen: showNewPopover,
    onOpen: openNewPopover,
    onClose: closeNewPopover,
  } = useDisclosure();

  const enableReplies = useFlag('enable-comment-replies');

  const commentsContext = useContext(CommentsContext);

  const {
    isOpen: newCommentOpen,
    onOpen: openNewComment,
    onClose: closeNewComment,
  } = useDisclosure();

  useHotkeys(
    'shift+c',
    () => {
      openNewComment();
      closeNewPopover();
    },
    {
      enabled: !newCommentOpen,
    }
  );

  useHotkeys(
    'esc',
    () => {
      closeNewComment();
    },
    {
      enabled: newCommentOpen,
    }
  );

  const [{ data: commentsDataQuery }] = useQuery({
    query: ListAllCommentsDocument,
  });

  const { projectId } = useParams<
    keyof ProjectPathParams
  >() as ProjectPathParams;

  const [numberOfUnreadComments, setNumberOfUnreadComments] = useState(0);

  const _buildComments = (closeNewPopover: () => void) => {
    if (!commentsDataQuery || !commentsDataQuery.comments) {
      return;
    }

    const commentMap = new Map<string, CommentDeepFragment[]>();

    commentsDataQuery?.comments.forEach((comment) => {
      const parentId = comment.parentId || '';
      if (!commentMap.has(parentId)) {
        commentMap.set(parentId, []);
      }
      commentMap.get(parentId)?.push(comment);
    });

    const topLevelComments = commentMap.get('') || [];

    return (
      topLevelComments
        .filter((comment) => {
          return comment.projectId === projectId;
        })
        .sort((a, b) => b.createdAtTz.localeCompare(a.createdAtTz))
        // eslint-disable-next-line complexity
        .map((comment, idx) => {
          const replies = commentMap.get(comment.id);
          return (
            <>
              {idx !== 0 ? <Divider /> : undefined}
              <Comment
                key={comment.id}
                id={comment.id}
                content={comment.content}
                createdAt={comment.createdAtTz}
                user={{ username: comment.username }}
                projectId={comment.projectId}
                takeoffId={comment.orderId ? comment.orderId : undefined}
                readBy={comment.readBy ? comment.readBy : []}
                closeParentPopover={closeNewPopover}
                elementIdentifiers={
                  comment.elementIdentifiers ? comment.elementIdentifiers : []
                }
                savedViews={comment.savedViews ? comment.savedViews : undefined}
                commentId={comment.id}
                replies={replies}
              />
            </>
          );
        })
    );
  };

  const AuthUser = useUserTenant().username;

  useEffect(() => {
    if (commentsDataQuery?.comments) {
      const numberOfUnreadComments2 = commentsDataQuery?.comments
        .filter((comment) => {
          return comment.projectId === projectId;
        })
        .filter(
          (comment) => !comment.readBy || !comment.readBy.includes(AuthUser)
        ).length;
      setNumberOfUnreadComments(numberOfUnreadComments2);
      return;
    }
    setNumberOfUnreadComments(0);
  }, [AuthUser, commentsDataQuery, projectId]);

  const { viewerMode } = useViewerMode();
  const toast = useToast();

  return (
    <>
      {newCommentOpen && (
        <NewCommentPopover
          isNewComment
          closeEditor={closeNewComment}
        ></NewCommentPopover>
      )}
      <Portal containerRef={commentsContext.headerRef}>
        <Popover
          isOpen={showNewPopover}
          onOpen={openNewPopover}
          onClose={closeNewPopover}
        >
          <PopoverTrigger>
            <Box position="relative">
              <IconButton
                size={'md'}
                aria-label="use-dark-mode"
                colorScheme="gray"
                variant={'ghost'}
                icon={
                  <>
                    <SparkelIcon icon={faComment} />
                    {numberOfUnreadComments > 0 ? (
                      <Badge
                        borderRadius={'2xl'}
                        colorScheme="red"
                        rounded={'3xl'}
                        position="absolute"
                        bottom={0.4}
                        right={0.4}
                        variant={'solid'}
                      >
                        {numberOfUnreadComments}
                      </Badge>
                    ) : null}
                  </>
                }
              ></IconButton>
            </Box>
          </PopoverTrigger>
          {/* This is a hacky way to ensure the popover is on top of everything */}
          <Portal containerRef={{ current: document.body }}>
            <Box position="relative" zIndex={99}>
              <PopoverContent
                width={'sm'}
                borderRadius={'2xl'}
                shadow={'xl'}
                position="relative"
              >
                <PopoverHeader pl={4} pr={2} border={'none'}>
                  <Flex justifyContent="space-between" alignItems="end">
                    <Heading size="sm" as="h3">
                      Comments
                    </Heading>
                    <Spacer />
                    <Tooltip label="Add comment (⇧C)">
                      <IconButton
                        icon={
                          <SparkelIcon
                            icon={faPlus}
                            color={['gray.50', 'gray.700']}
                          />
                        }
                        colorScheme="brand"
                        aria-label="Add comment"
                        size="xs"
                        borderRadius="xl"
                        marginLeft={2}
                        onClick={() => {
                          if (viewerMode === ViewerMode.Models) {
                            openNewComment();
                            closeNewPopover();
                          } else {
                            toast({
                              title:
                                'Comments not yet supported for sheets, only models',
                              status: 'error',
                            });
                          }
                        }}
                      />
                    </Tooltip>
                  </Flex>
                </PopoverHeader>
                <PopoverBody
                  overflowY="auto"
                  maxHeight="80vh"
                  paddingTop={0}
                  paddingX={2}
                >
                  <Divider />
                  {_buildComments(closeNewPopover)}
                </PopoverBody>
              </PopoverContent>
            </Box>
          </Portal>
        </Popover>
      </Portal>
    </>
  );
}
